import React from 'react';
import { 
  FileText, 
  Scale, 
  Leaf, 
  Cpu, 
  Database, 
  Users, 
  Globe,
  Shield,
  Target,
  CheckCircle,
  Zap,
  Network
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Services = () => {
  const aiPolicyServices = [
    'National AI strategies and roadmaps',
    'Public sector AI ethics and governance frameworks',
    'Organisational AI governance operating models',
    'AI maturity and capability assessment frameworks',
    'Risk-based AI governance aligned with emerging regulation',
    'AI safety, assurance, and guardrail frameworks'
  ];

  const standardsServices = [
    'Mapping AI systems and use cases to international AI standards',
    'Regulatory readiness assessments for emerging AI and data laws',
    'Alignment with risk-based AI regulatory approaches',
    'Support for standards development and consultation processes',
    'Integration of AI governance with enterprise risk and assurance'
  ];

  const sustainabilityServices = [
    'AI for sustainability and climate action governance',
    'Responsible AI for environmental monitoring and remote sensing',
    'AI impact assessment aligned with social and environmental outcomes',
    'Integration of AI governance into ESG and CSR strategies'
  ];

  const emergingTechnologies = [
    'Generative AI and foundation models',
    'Robotics and autonomous systems',
    'Advanced data analytics and data science',
    'Machine learning and decision-support systems',
    'Computer vision and remote sensing',
    'AI-enabled digital twins',
    'Internet of Things (IoT) and cyber-physical systems',
    'Edge AI and intelligent infrastructure',
    'AI in critical and safety-sensitive domains',
    'Human–AI collaboration and augmentation'
  ];

  const dataGovernanceServices = [
    'Data governance and data lifecycle management',
    'Cybersecurity and system resilience',
    'Enterprise risk management and internal controls',
    'Auditability, traceability, and accountability mechanisms',
    'Assurance frameworks for AI systems'
  ];

  const collaborationServices = [
    'Advisory support for multilateral and cross-border initiatives',
    'Policy and standards co-development',
    'Capacity building and institutional strengthening',
    'Stakeholder engagement and consultation facilitation',
    'Research collaboration and evidence-based policy support'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight mb-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
              Our
              <span className="block font-normal mt-2">Services</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed font-medium">
              Comprehensive Capabilities for Responsible, Sustainable, and Trustworthy AI
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI provides a comprehensive suite of services supporting the design, implementation, and assurance of ethical, responsible, and sustainable artificial intelligence across public, private, and civil society contexts. Our services span the full AI policy, governance, and implementation lifecycle, enabling governments, organisations, and international partners to translate global principles into practical, context-aware outcomes.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our work is informed by deep expertise across international AI policy, standards development, regulatory frameworks, sustainability, data governance, cybersecurity, and emerging technologies. We operate as a neutral, evidence-based partner, supporting collaboration across governments, multilateral institutions, standards bodies, industry, and research communities.
            </p>
          </div>
        </div>
      </section>

      {/* AI Policy, Strategy, and Governance */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  AI Policy, Strategy, and Governance Services
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We support the development and implementation of AI policies, strategies, and governance frameworks at national, sectoral, and organisational levels.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our work is informed by global best practices and policy frameworks developed by international bodies such as the OECD, UNESCO, and the European Union, as well as standards and guidance from the IEEE Standards Association, ISO, and the National Institute of Standards and Technology.
              </p>
            </div>
            <div className="space-y-3">
              {aiPolicyServices.map((service, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-gray-700">{service}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Standards Alignment */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <Scale className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              Standards Alignment and Regulatory Readiness
            </h2>
          </div>
          
          <div className="max-w-4xl mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We help organisations and governments align AI initiatives with international standards, voluntary frameworks, and emerging regulatory regimes, ensuring coherence, defensibility, and interoperability.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This includes alignment with data protection, data governance, and cybersecurity frameworks, such as the General Data Protection Regulation and ISO/IEC 27001, recognising that trustworthy AI depends on strong foundations in data quality, privacy, security, and resilience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {standardsServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{service}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability, SDGs */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <Leaf className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  Sustainability, SDGs, and Responsible Innovation
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Sustainability is embedded across all our services. We support organisations and governments in aligning AI initiatives with the United Nations Sustainable Development Goals, ensuring AI contributes positively to environmental sustainability, social equity, and inclusive economic development.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We work across development, humanitarian, and public-interest contexts, supporting inclusive and responsible AI adoption—particularly in developing and emerging economies.
              </p>
            </div>
            <div className="space-y-4">
              {sustainabilityServices.map((service, index) => (
                <div key={index} className="flex items-start gap-4 p-5 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-gray-700 pt-2">{service}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Emerging Technologies */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <Cpu className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              Emerging Technologies and Applied AI Domains
            </h2>
          </div>
          
          <div className="max-w-4xl mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI maintains a broad and current understanding of AI-enabled and AI-adjacent emerging technologies, enabling governance and policy to keep pace with innovation.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We support policy, governance, and engineering-aligned approaches to these technologies across a wide portfolio of industries, including government, infrastructure, health, finance, energy, environment, telecommunications, and humanitarian sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {emergingTechnologies.map((tech, index) => (
              <div key={index} className="p-4 bg-white rounded-lg border hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-black flex-shrink-0" />
                  <p className="text-sm text-gray-700">{tech}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Governance, Cybersecurity */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <Database className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  Data Governance, Cybersecurity, Risk, and Assurance
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Recognising that AI governance is inseparable from broader organisational controls, we provide services that integrate AI ethics with foundational governance and security requirements.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed italic">
                This integrated approach enables organisations to move beyond fragmented compliance towards coherent, scalable, and sustainable governance architectures.
              </p>
            </div>
            <div className="space-y-3">
              {dataGovernanceServices.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5" />
                      </div>
                      <p className="text-gray-700">{service}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Collaboration Section */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                  <Users className="w-7 h-7 text-black" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  Collaboration, Capacity Building, and Partnership
                </h2>
              </div>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                The Centre for Sustainable AI operates as a collaborative partner, working alongside governments, international organisations, standards bodies, research institutions, and industry leaders.
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
                We see AI governance as a shared global responsibility. Our role is to contribute expertise, structure, and insight—supporting collective efforts toward a safe, responsible, and sustainable AI future.
              </p>
            </div>
            <div className="space-y-4">
              {collaborationServices.map((service, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-white/5 rounded-lg">
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Globe className="w-4 h-4 text-black" />
                  </div>
                  <p className="text-gray-200">{service}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
